(function() {
  'use strict';

  const normalize = (value) => String(value || '').trim().toLowerCase();
  const numericId = (value) => /^\d+$/.test(String(value || '').split('/').pop())
    ? String(value).split('/').pop() : null;
  const marker = (item) => item.properties?._discountGWP || item.properties?._discount_gift || item.properties?.Gift;
  const giftKey = (variant, title) => `${variant}:${normalize(title)}`;
  const products = (config) => ['product', 'product2', 'product3'].map((key) => config[key]).filter(Boolean);

  function planAutomaticGifts({ cart, promotions, country, giftLinePrices = {} }, now = Date.now()) {
    const automatic = promotions.filter((meta) => meta?.title && String(meta.discountId).includes('/DiscountAutomaticNode/'));
    const byTitle = new Map();
    automatic.forEach((meta) => {
      const title = normalize(meta.title);
      byTitle.set(title, byTitle.has(title) ? null : meta);
    });
    // Function discounts can have an empty application type in both Liquid and Ajax.
    // Match those against the automatic definitions, without guessing on code collisions.
    promotions.filter((meta) => meta && !automatic.includes(meta)).forEach((meta) => {
      [meta.title, meta.code, ...(meta.codes || [])].forEach((value) => {
        if (byTitle.has(normalize(value))) byTitle.set(normalize(value), null);
      });
    });
    const applications = [
      ...(cart.cart_level_discount_applications || []),
      ...cart.items.flatMap((item) => (item.line_level_discount_allocations || item.discount_allocations || []).map((entry) => entry.discount_application))
    ];
    const appliedCodes = new Set([
      ...(cart.discount_codes || []).filter((entry) => entry.applicable).map((entry) => normalize(entry.code)),
      ...applications.filter((entry) => entry?.type === 'discount_code').map((entry) => normalize(entry.title))
    ]);
    const paidItems = cart.items.filter((item) => !marker(item));
    const desired = new Map();

    byTitle.forEach((meta, title) => {
      if (!meta || appliedCodes.has(title)) return;
      if (meta.startsAt && !(Date.parse(meta.startsAt) <= now)) return;
      if (meta.endsAt && !(Date.parse(meta.endsAt) > now)) return;
      if (meta.availableCountries?.length && !meta.availableCountries.includes(country)) return;
      // Only matching purchases count toward the configured spend and gift tiers.
      const qualifyingItems = paidItems.filter((item) => {
        const subscription = Boolean(item.selling_plan_allocation);
        if (meta.purchaseType === 'subscription') return subscription;
        if (['regular', 'one_time_purchase'].includes(meta.purchaseType)) return !subscription;
        return true;
      });
      if (!qualifyingItems.length) return;
      const subtotal = qualifyingItems.reduce((total, item) => total + Number(item.final_line_price ?? item.original_line_price ?? 0), 0) / 100;
      let config = meta;
      if (meta.isTieredDiscount) {
        // Match checkout: higher discount-only tiers keep the qualifying gift tier.
        config = [...(meta.discountTiers || [])]
          .filter((tier) => (tier.addGWP || tier.addGwp) && Number.isFinite(Number(tier.minimum)) && subtotal >= Number(tier.minimum))
          .sort((a, b) => Number(b.minimum) - Number(a.minimum))[0];
        if (!config || !(config.addGWP || config.addGwp)) return;
      } else {
        const minimum = Number(meta.minimum || 0);
        if (!Number.isFinite(minimum) || minimum < 0 || subtotal < minimum) return;
      }
      products(config).forEach((product) => {
        const id = numericId(product.variantId);
        const quantity = Number(product.quantity || 1);
        if (!id || !Number.isSafeInteger(quantity) || quantity < 1) return;
        const key = giftKey(id, title);
        const existing = desired.get(key);
        desired.set(key, { id, quantity: quantity + (existing?.quantity || 0), title: meta.title, discountId: meta.discountId });
      });
    });

    const updates = {};
    const rejected = [];
    cart.items.forEach((item) => {
      const title = normalize(marker(item));
      const owned = Boolean(item.properties?._automaticGWP);
      if (!owned && byTitle.has(title) && byTitle.get(title) === null) return;
      const known = byTitle.has(title) && byTitle.get(title) !== null && !appliedCodes.has(title);
      if (!owned && (!title || !known)) return;
      const key = giftKey(item.variant_id, title);
      const target = desired.get(key);
      const matchesOwner = !owned || item.properties._automaticGWP === target?.discountId;
      const linePrice = Number(giftLinePrices[item.key] ?? item.final_line_price ?? (item.final_price != null ? item.final_price * item.quantity : item.original_line_price));
      const free = Number.isFinite(linePrice) && linePrice === 0;
      if (target && matchesOwner && !free) rejected.push({ id: String(item.variant_id), title });
      const quantity = matchesOwner && free ? Math.min(item.quantity, target?.quantity || 0) : 0;
      if (quantity !== item.quantity) updates[item.key] = quantity;
      if (target && matchesOwner) target.quantity -= quantity;
    });
    const additions = [...desired.values()].filter((entry) => entry.quantity > 0);
    const fingerprint = JSON.stringify({
      paid: paidItems.map((item) => [item.key, item.quantity, item.original_line_price, item.final_line_price]),
      applications: [...new Set(applications
        .filter((entry) => entry && (entry.type === 'discount_code' || !byTitle.has(normalize(entry.title))))
        .map((entry) => JSON.stringify([entry.type, normalize(entry.title), entry.value_type, entry.value])))].sort(),
      codes: cart.discount_codes || [], promotions, country, currency: cart.currency
    });
    return { updates, additions, rejected, fingerprint };
  }

  if (typeof module === 'object' && module.exports) {
    module.exports = { planAutomaticGifts };
    return;
  }

  const root = window.Shopify.routes.root;
  let pending = false;
  let running = null;
  let timer;
  let dispatching = false;
  const failedAdds = new Map();
  const checkoutForms = new WeakSet();

  async function request(path, body, signal) {
    const response = await fetch(root + path, {
      credentials: 'same-origin', cache: 'no-store', signal,
      ...(body ? {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
      } : {})
    });
    const data = await response.json();
    if (!response.ok || data.status >= 400) {
      throw Object.assign(new Error(data.description || 'Automatic gift request failed'), {
        status: response.status || Number(data.status)
      });
    }
    return data;
  }

  function readState(signal) {
    return request(`cart?view=automatic-gwp&t=${Date.now()}`, null, signal);
  }

  async function rebuildCart() {
    const validations = [];
    dispatching = true;
    try {
      document.dispatchEvent(new CustomEvent('cart:build', {
        detail: { waitUntil: (validation) => validations.push(validation) }
      }));
    } finally { dispatching = false; }
    let deadline;
    try {
      return await Promise.race([
        Promise.all(validations),
        new Promise((resolve, reject) => { deadline = setTimeout(() => reject(new Error('Cart validation timed out')), 5000); })
      ]);
    } finally { clearTimeout(deadline); }
  }

  async function reconcile(signal) {
    let changed = false;
    let settled = false;
    const attemptedAdds = new Set();
    try {
      for (let step = 0; step < 12; step++) {
        const state = await readState(signal);
        if (!Array.isArray(state.cart?.items) || !Array.isArray(state.promotions)) throw new Error('Invalid automatic gift state');
        const plan = planAutomaticGifts(state);
        plan.rejected.forEach((gift) => failedAdds.set(giftKey(gift.id, gift.title), plan.fingerprint));
        if (Object.keys(plan.updates).length) {
          changed = true;
          await request('cart/update.js', { updates: plan.updates }, signal);
          continue;
        }
        const gift = plan.additions.find((entry) => !attemptedAdds.has(giftKey(entry.id, entry.title)) &&
          failedAdds.get(giftKey(entry.id, entry.title)) !== plan.fingerprint);
        if (!gift) { settled = true; break; }
        attemptedAdds.add(giftKey(gift.id, gift.title));
        try {
          await request('cart/add.js', { items: [{
            id: gift.id, quantity: gift.quantity,
            properties: { Gift: gift.title, _discountGWP: gift.title, _automaticGWP: gift.discountId }
          }] }, signal);
          changed = true;
        } catch (error) {
          if (signal.aborted) {
            changed = true; // An aborted response does not prove the add was rejected.
            throw error;
          }
          if (error.status === 400 || error.status === 422) {
            failedAdds.set(giftKey(gift.id, gift.title), plan.fingerprint);
          }
          console.warn('Unable to add automatic gift:', error);
          // Shopify can add the available units and still return a stock error.
          try {
            const latest = await readState(signal);
            changed = changed || JSON.stringify(latest.cart?.items) !== JSON.stringify(state.cart.items);
          } catch (readError) {
            changed = true; // Refresh conservatively if partial success cannot be checked.
            throw readError;
          }
        }
      }
      if (!settled) throw new Error('Automatic gift synchronization did not settle');
    } finally {
      if (changed) {
        let cart;
        try {
          const latest = await readState(signal);
          if (Array.isArray(latest.cart?.items)) cart = latest.cart;
        } catch (error) {
          console.warn('Unable to read automatic gift changes:', error);
        }
        // Suppress only our synchronous notifications, never real events during a read.
        dispatching = true;
        try {
          if (cart) document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, automaticGWP: true } }));
        } finally { dispatching = false; }
        await rebuildCart();
      }
    }
  }

  function sync() {
    clearTimeout(timer);
    pending = true;
    if (!running) {
      const controller = new AbortController();
      const deadline = setTimeout(() => controller.abort(), 5000);
      running = (async () => {
        while (pending) {
          pending = false;
          await reconcile(controller.signal);
        }
        return true;
      })().catch((error) => {
        console.warn('Unable to synchronize automatic gifts:', error);
        return false;
      })
        .finally(() => {
          clearTimeout(deadline);
          running = null;
          if (pending) schedule();
        });
    }
    return running;
  }

  function schedule() {
    if (dispatching) return;
    clearTimeout(timer);
    timer = setTimeout(sync, 80);
  }

  ['cart:init', 'cart:updated', 'cart:build', 'cart:open', 'ajaxProduct:added', 'ajaxProduct:removed']
    .forEach((name) => document.addEventListener(name, schedule));
  window.addEventListener('pageshow', schedule);
  window.addEventListener('focus', () => { failedAdds.clear(); schedule(); });
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    const button = event.submitter;
    if (!button?.matches('[name="checkout"], .cart__checkout') || checkoutForms.has(form)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const wasDisabled = button.disabled;
    button.disabled = true;
    let blocked = wasDisabled;
    let ready = false;
    try {
      if (!await sync()) return;
      const validations = await rebuildCart();
      blocked = blocked || validations.some((validation) => validation?.form === form && validation.disabled);
      ready = true;
    } catch (error) {
      console.warn('Unable to validate cart before checkout:', error);
    } finally {
      blocked = blocked || form.querySelector?.('.js-cart-limit-hazardous-products')?.dataset.visible === 'true';
      button.disabled = blocked;
    }
    if (ready && !blocked) {
      checkoutForms.add(form);
      try {
        if (typeof form.requestSubmit === 'function') {
          try { form.requestSubmit(button); } catch (error) { button.click(); }
        } else {
          button.click();
        }
      } finally { checkoutForms.delete(form); }
    }
  }, true);

  window.SDG = window.SDG || {};
  window.SDG.automaticGWP = { sync };
  schedule();
})();
